(() => {
  const VERSION = '2026.08.25.3';
  const previous = window.BHCartRecommendations;
  if (previous?.version === VERSION) {
    previous.refresh?.();
    return;
  }
  previous?.controller?.abort?.();
  const controller = new AbortController();
  const signal = controller.signal;

  const root = window.Shopify?.routes?.root || '/';
  const shopRoot = root.endsWith('/') ? root : `${root}/`;
  const formatter = new Intl.NumberFormat(document.documentElement.lang || 'es-PE', {
    style: 'currency', currency: window.Shopify?.currency?.active || 'PEN'
  });
  const state = {
    cart: null, products: [], sections: { primary: [], secondary: [] },
    loading: null, rendering: false, adding: false, previousFocus: null, closeTimer: 0
  };
  const RECENT_KEY = 'bh:recent-product-ids';
  const escapeHTML = value => String(value ?? '').replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);
  const money = cents => formatter.format(Number(cents || 0) / 100).replace(/\s/g, ' ');
  const normalize = value => String(value || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const CATEGORY_RULES = [
    ['protector', /protector|forro de colchon|antifluid/],
    ['sabana', /sabana|sheet/],
    ['edredon', /edredon|duvet|comforter|set de cama/],
    ['almohada', /almohada|funda de almohada|pillow/],
    ['manta', /manta|frazada|cobertor|blanket/],
    ['bano', /toalla|bata|salida de bano|tapete de bano/],
    ['mueble', /forro de mueble|forro de silla|sofa|sillon/],
    ['kids', /kids|nino|nina|bebe|infantil/]
  ];
  const COMPLEMENTS = {
    sabana: ['protector', 'edredon', 'almohada', 'manta'],
    protector: ['sabana', 'edredon', 'almohada'],
    edredon: ['sabana', 'protector', 'almohada', 'manta'],
    almohada: ['sabana', 'edredon', 'manta'],
    manta: ['sabana', 'almohada', 'edredon'],
    bano: ['bano'],
    mueble: ['mueble'],
    kids: ['kids', 'sabana', 'almohada']
  };

  function productText(product) {
    const tags = Array.isArray(product?.tags) ? product.tags.join(' ') : product?.tags || '';
    return normalize([product?.title, product?.product_type, product?.vendor, tags].filter(Boolean).join(' '));
  }

  function category(value) {
    const text = typeof value === 'string' ? normalize(value) : productText(value);
    return CATEGORY_RULES.find(([, pattern]) => pattern.test(text))?.[0] || 'otros';
  }

  function cartProfile() {
    const categories = new Set();
    const desired = new Set();
    const terms = new Set();
    const optionValues = new Set();

    (state.cart?.items || []).forEach(item => {
      const itemText = normalize([item.product_title, item.title, item.product_type, item.vendor].filter(Boolean).join(' '));
      const itemCategory = category(itemText);
      categories.add(itemCategory);
      (COMPLEMENTS[itemCategory] || []).forEach(value => desired.add(value));
      itemText.split(' ').filter(term => term.length > 3).forEach(term => terms.add(term));
      (item.options_with_values || []).forEach(option => optionValues.add(normalize(option.value)));
    });

    return { categories, desired, terms, optionValues };
  }

  function recentIds() {
    try {
      const ids = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(ids) ? ids.map(Number).filter(Boolean).slice(0, 8) : [];
    } catch (_) { return []; }
  }

  function rememberProduct() {
    const id = Number(window.meta?.product?.id || window.ShopifyAnalytics?.meta?.product?.id || 0);
    if (!id) return;
    try { localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...recentIds().filter(item => item !== id)].slice(0, 8))); }
    catch (_) {}
  }

  function variants(product) {
    const affinity = new Set((state.cart?.items || []).flatMap(item =>
      (item.options_with_values || []).map(option => String(option.value).trim().toLowerCase())
    ));
    return (product?.variants || []).filter(variant => variant.available !== false).sort((a, b) => {
      const score = variant => (variant.options || []).filter(value => affinity.has(String(value).trim().toLowerCase())).length;
      return score(b) - score(a);
    });
  }

  function price(product) {
    const prices = variants(product).map(variant => Math.round(Number(variant.price || 0))).filter(Boolean);
    return prices.length ? Math.min(...prices) : 0;
  }

  function pricing(product) {
    const available = variants(product);
    const chosen = available.reduce((best, variant) => {
      const variantPrice = Math.round(Number(variant.price || 0));
      if (!variantPrice) return best;
      if (!best || variantPrice < best.price) {
        return {
          price: variantPrice,
          compareAt: Math.round(Number(variant.compare_at_price || variant.compareAtPrice || 0))
        };
      }
      return best;
    }, null);
    return chosen || { price: 0, compareAt: 0 };
  }

  function image(product) {
    const value = product?.featured_image || product?.images?.[0] || '';
    return typeof value === 'string' ? value : value?.url || value?.src || '';
  }

  function url(product) {
    return product?.url || (product?.handle ? `${shopRoot}products/${product.handle}` : '#');
  }

  function productScore(product) {
    const profile = cartProfile();
    const productCategory = category(product);
    const text = productText(product);
    const ratio = state.cart?.total_price ? price(product) / state.cart.total_price : 1;
    let points = product._bhIntent === 'complementary' ? 90 : 42;
    if (profile.desired.has(productCategory)) points += 52;
    if (profile.categories.has(productCategory) && !profile.desired.has(productCategory)) points -= 22;
    if (product._bhSource === 'recent') points += 8;
    if (ratio >= .08 && ratio <= .55) points += 24;
    else if (ratio > .55 && ratio <= .9) points += 10;
    else if (ratio > 1.25) points -= 20;
    if (/bambu|descanso|dormitorio|textil|hipoalergen/.test(text)) points += 8;
    points += Math.min(12, [...profile.terms].filter(term => text.includes(term)).length * 3);
    if (variants(product).some(variant => (variant.options || []).some(value => profile.optionValues.has(normalize(value))))) points += 12;
    return points;
  }

  async function recommendations(productId, intent, source = 'cart') {
    try {
      const endpoint = new URL(`${shopRoot}recommendations/products.json`, location.origin);
      endpoint.searchParams.set('product_id', productId);
      endpoint.searchParams.set('limit', '10');
      endpoint.searchParams.set('intent', intent);
      const response = await fetch(endpoint, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return [];
      const data = await response.json();
      return (data.products || []).map(product => ({ ...product, _bhIntent: intent, _bhSource: source }));
    } catch (_) { return []; }
  }

  async function catalog() {
    try {
      const response = await fetch(`${shopRoot}collections/all/products.json?limit=50`, {
        headers: { Accept: 'application/json' }, credentials: 'same-origin'
      });
      if (!response.ok) return [];
      const data = await response.json();
      return (data.products || []).map(product => ({ ...product, _bhIntent: 'related', _bhSource: 'catalog' }));
    } catch (_) { return []; }
  }

  function unique(list, excluded = new Set()) {
    const result = new Map();
    list.forEach(product => {
      const id = Number(product?.id || 0);
      if (!id || excluded.has(id) || result.has(id) || !variants(product).length) return;
      result.set(id, product);
    });
    return [...result.values()].sort((a, b) => productScore(b) - productScore(a));
  }

  function diversify(list, limit) {
    const selected = [];
    const deferred = [];
    const seenCategories = new Set();
    list.forEach(product => {
      const key = category(product);
      if (!seenCategories.has(key)) {
        seenCategories.add(key);
        selected.push(product);
      } else deferred.push(product);
    });
    return [...selected, ...deferred].slice(0, limit);
  }

  function recommendationReason(product) {
    const profile = cartProfile();
    const productCategory = category(product);
    if (profile.desired.has(productCategory)) return 'Complemento para tu compra';
    if (product._bhIntent === 'complementary') return 'Combina con tu carrito';
    if (product._bhSource === 'recent') return 'Según lo que viste';
    return 'Recomendado para ti';
  }

  function categoryLabel(product) {
    return ({
      protector: 'Protección', sabana: 'Sábanas', edredon: 'Ropa de cama',
      almohada: 'Almohadas', manta: 'Mantas', bano: 'Baño',
      mueble: 'Hogar', kids: 'Kids', otros: 'Bambú Home'
    })[category(product)] || 'Bambú Home';
  }

  async function load(force = false) {
    if (state.loading && !force) return state.loading;
    if (state.products.length && !force) return state.sections;
    state.loading = (async () => {
      const response = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error('No se pudo leer el carrito.');
      state.cart = await response.json();
      if (!state.cart.items?.length) return (state.sections = { primary: [], secondary: [] });

      const cartIds = [...new Set(state.cart.items.map(item => Number(item.product_id)).filter(Boolean))];
      const excluded = new Set(cartIds);
      const batches = await Promise.all([
        ...cartIds.slice(0, 4).flatMap(id => [recommendations(id, 'complementary'), recommendations(id, 'related')]),
        ...recentIds().filter(id => !excluded.has(id)).slice(0, 3).map(id => recommendations(id, 'related', 'recent')),
        catalog()
      ]);
      const all = batches.flat();
      const profile = cartProfile();
      const professionallyMatched = all.filter(product =>
        product._bhIntent === 'complementary' || profile.desired.has(category(product))
      );
      let primary = diversify(unique(professionallyMatched, excluded), 8);
      if (primary.length < 4) {
        const used = new Set([...excluded, ...primary.map(product => Number(product.id))]);
        primary = [...primary, ...diversify(unique(all, used), 4 - primary.length)];
      }
      const used = new Set([...excluded, ...primary.map(product => Number(product.id))]);
      let secondary = diversify(unique(all.filter(product => product._bhIntent !== 'complementary'), used), 10);
      if (!secondary.length) secondary = diversify(unique(all, used), 10);
      if (!secondary.length && primary.length > 3) secondary = primary.splice(Math.ceil(primary.length / 2));

      state.sections = { primary, secondary };
      state.products = [...primary, ...secondary];
      return state.sections;
    })().finally(() => { state.loading = null; });
    return state.loading;
  }

  function tile(product) {
    const available = variants(product);
    const multiple = available.length > 1;
    const productPricing = pricing(product);
    const hasDiscount = productPricing.compareAt > productPricing.price;
    const discount = hasDiscount
      ? Math.max(1, Math.round((1 - productPricing.price / productPricing.compareAt) * 100))
      : 0;
    return `
      <article class="bh-cart-reco-tile" data-bh-reco-product="${escapeHTML(product.id)}">
        <a class="bh-cart-reco-tile__media" href="${escapeHTML(url(product))}">
          ${image(product) ? `<img src="${escapeHTML(image(product))}" alt="${escapeHTML(product.title)}" width="420" height="315" loading="lazy" decoding="async">` : '<span class="bh-cart-reco-tile__placeholder"></span>'}
          <span class="bh-cart-reco-tile__badge">${product._bhIntent === 'complementary' ? 'Complemento' : 'Para ti'}</span>
          ${discount ? `<span class="bh-cart-reco-tile__discount">-${discount}%</span>` : ''}
        </a>
        <div class="bh-cart-reco-tile__body">
          <div class="bh-cart-reco-tile__meta">${escapeHTML(categoryLabel(product))}</div>
          <a
            class="bh-cart-reco-tile__title"
            href="${escapeHTML(url(product))}"
            title="${escapeHTML(product.title)}"
          >${escapeHTML(product.title)}</a>
          <div class="bh-cart-reco-tile__pricing">
            <strong>${multiple ? 'Desde ' : ''}${money(productPricing.price)}</strong>
            ${hasDiscount ? `<s>${money(productPricing.compareAt)}</s>` : ''}
          </div>
          <button class="bh-cart-reco-tile__button" type="button" data-bh-reco-quick data-product-id="${product.id}"
            ${multiple ? '' : `data-variant-id="${available[0]?.id || ''}"`}>
            <span>${multiple ? 'Elegir opciones' : 'Agregar al carrito'}</span><span aria-hidden="true">→</span>
          </button>
        </div>
      </article>`;
  }

  function shelf(id, title, subtitle, products) {
    if (!products.length) return '';
    const eyebrow = id === 'BHCartComplements' ? 'Compatibles con tu compra' : 'Descubre algo más';
    return `
      <section class="bh-cart-reco-shelf" aria-labelledby="${id}-title">
        <header class="bh-cart-reco-shelf__header">
          <div><span class="bh-cart-reco-shelf__eyebrow">${eyebrow}</span><h3 id="${id}-title">${title}</h3><p>${subtitle}</p></div>
          <div class="bh-cart-reco-shelf__controls">
            <button type="button" data-bh-reco-scroll="-1" data-track="${id}" aria-label="Productos anteriores">←</button>
            <button type="button" data-bh-reco-scroll="1" data-track="${id}" aria-label="Más productos">→</button>
          </div>
        </header>
        <div class="bh-cart-reco-shelf__track" id="${id}" tabindex="0">${products.map(tile).join('')}</div>
      </section>`;
  }

  const loading = () => `<div class="bh-cart-reco-loading bh-cart-reco-loading--inline" role="status"><span class="bh-cart-reco-spinner"></span><span>Preparando recomendaciones…</span></div>`;

  function mountPoint() {
    const explicit = document.querySelector('[data-bh-cart-recommendations-mount]');
    if (explicit) return explicit;
    const host = document.querySelector('cart-items.bh-cart-items .page-width');
    if (host) {
      const mount = document.createElement('div');
      mount.dataset.bhCartRecommendationsMount = '';
      host.appendChild(mount);
      return mount;
    }
    const drawer = document.querySelector('#CartDrawer-CartItems');
    if (!drawer) return null;
    const mount = document.createElement('div');
    mount.dataset.bhCartRecommendationsMount = '';
    drawer.insertAdjacentElement('afterend', mount);
    return mount;
  }

  async function render() {
    const mount = mountPoint();
    if (!mount || mount.dataset.bhRecoReady === 'true' || state.rendering || mount.closest('.is-empty')) return;
    state.rendering = true;
    mount.innerHTML = loading();
    try {
      const sections = await load();
      if (!mount.isConnected) return;
      if (!sections.primary.length && !sections.secondary.length) {
        mount.replaceChildren();
      } else {
        mount.innerHTML = `
          <aside class="bh-cart-reco-hub" data-bh-cart-reco-entry>
            ${shelf('BHCartComplements', 'Completa tu compra', 'Complementos compatibles elegidos para tu carrito.', sections.primary)}
            ${shelf('BHCartSuggestions', 'También podría gustarte', 'Más opciones para tu hogar y descanso.', sections.secondary)}
          </aside>`;
      }
      mount.dataset.bhRecoReady = 'true';
    } catch (error) {
      mount.innerHTML = '<p class="bh-cart-reco-error">No pudimos cargar las recomendaciones.</p>';
      console.warn('[BH Cart Recommendations]', error);
    } finally { state.rendering = false; }
  }

  function modalCard(product) {
    return `<article class="bh-cart-reco-card" data-bh-reco-product="${product.id}">
      ${image(product) ? `<img class="bh-cart-reco-card__image" src="${escapeHTML(image(product))}" alt="${escapeHTML(product.title)}">` : ''}
      <div><h3 class="bh-cart-reco-card__title">${escapeHTML(product.title)}</h3>
      <p class="bh-cart-reco-card__reason">${escapeHTML(recommendationReason(product))}. Elige la opción que prefieres.</p>
      <div class="bh-cart-reco-card__actions"><select class="bh-cart-reco-card__variant" aria-label="Elige una opción">
      ${variants(product).map(variant => `<option value="${variant.id}">${escapeHTML(variant.title)} · ${money(variant.price)}</option>`).join('')}
      </select><button class="bh-cart-reco-card__add" type="button" data-bh-reco-add>Agregar</button></div></div></article>`;
  }

  function ensureModal() {
    let modal = document.querySelector('[data-bh-cart-reco-modal]');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'bh-cart-reco-modal';
    modal.hidden = true;
    modal.dataset.bhCartRecoModal = '';
    modal.innerHTML = `<section class="bh-cart-reco-modal__panel" role="dialog" aria-modal="true" aria-labelledby="BHCartRecoTitle">
      <header class="bh-cart-reco-modal__header"><div><span class="bh-cart-reco-hub__eyebrow">Último paso</span>
      <h2 class="bh-cart-reco-modal__title" id="BHCartRecoTitle">Elige tu opción</h2>
      <p class="bh-cart-reco-modal__subtitle">Confirma medida, color o presentación.</p></div>
      <button class="bh-cart-reco-modal__close" type="button" aria-label="Cerrar" data-bh-reco-close>×</button></header>
      <div class="bh-cart-reco-grid" data-bh-reco-grid></div></section>`;
    document.body.appendChild(modal);
    return modal;
  }

  function close() {
    const modal = document.querySelector('[data-bh-cart-reco-modal]');
    if (!modal || modal.hidden) return;
    clearTimeout(state.closeTimer);
    modal.classList.remove('is-open');
    document.documentElement.classList.remove('bh-cart-reco-open');
    state.closeTimer = setTimeout(() => { modal.hidden = true; state.previousFocus?.focus?.(); }, 280);
  }

  async function open(productId) {
    const modal = ensureModal();
    const grid = modal.querySelector('[data-bh-reco-grid]');
    state.previousFocus = document.activeElement;
    modal.hidden = false;
    document.documentElement.classList.add('bh-cart-reco-open');
    requestAnimationFrame(() => requestAnimationFrame(() => modal.classList.add('is-open')));
    grid.innerHTML = loading();
    await load();
    const product = state.products.find(item => Number(item.id) === Number(productId));
    grid.innerHTML = product ? modalCard(product) : '<p>Esta opción ya no está disponible.</p>';
    modal.querySelector('[data-bh-reco-close]')?.focus();
  }

  async function currentCart() {
    const response = await fetch(`${shopRoot}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('No se pudo actualizar el carrito.');
    return response.json();
  }

  async function refreshCartSurface(cart) {
    const recommendationAnchor = document.querySelector('[data-bh-cart-recommendations-mount]');
    const anchorTop = recommendationAnchor?.getBoundingClientRect().top;
    try {
      const pageResponse = await fetch(window.location.href, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (pageResponse.ok) {
        const nextDocument = new DOMParser().parseFromString(await pageResponse.text(), 'text/html');
        ['#main-cart-items', '#main-cart-footer', '#CartDrawer', '#cart-icon-bubble'].forEach(selector => {
          const current = document.querySelector(selector);
          const replacement = nextDocument.querySelector(selector);
          if (current && replacement) current.replaceWith(document.importNode(replacement, true));
        });
      }
    } catch (error) {
      console.warn('[BH Cart Recommendations] No se pudo refrescar el HTML parcial.', error);
    }

    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, source: 'bh-recommendations' } }));
    document.dispatchEvent(new CustomEvent('bh:cart:network-complete', { detail: { cart, source: 'bh-recommendations' } }));
    document.dispatchEvent(new CustomEvent('theme:cart:update', { detail: { cart, source: 'bh-recommendations' } }));
    window.BHCartUI?.refresh?.();

    if (recommendationAnchor?.isConnected && Number.isFinite(anchorTop)) {
      const difference = recommendationAnchor.getBoundingClientRect().top - anchorTop;
      if (Math.abs(difference) > 1) window.scrollBy({ top: difference, left: 0, behavior: 'auto' });
    }
  }

  async function add(button, explicitId) {
    const card = button.closest('[data-bh-reco-product]');
    const variantId = Number(explicitId || button.dataset.variantId || card?.querySelector('.bh-cart-reco-card__variant')?.value);
    if (!variantId || button.disabled || state.adding) return;
    const original = button.textContent;
    let added = false;
    state.adding = true;
    button.disabled = true;
    button.classList.add('is-loading');
    button.textContent = 'Agregando…';
    try {
      const response = await fetch(`${shopRoot}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin', body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      if (!response.ok) throw new Error('No se pudo agregar.');
      added = true;
      button.classList.remove('is-loading');
      button.classList.add('is-added');
      button.textContent = 'Agregado ✓';
      const cart = await currentCart();
      state.cart = cart;
      await refreshCartSurface(cart);
      close();
      refresh();
    } catch (error) {
      button.classList.remove('is-loading');
      if (added) {
        button.classList.add('is-added');
        button.textContent = 'Agregado ✓';
        document.dispatchEvent(new CustomEvent('bh:cart:network-complete', {
          detail: { source: 'bh-recommendations' }
        }));
        close();
        refresh();
      } else {
        button.disabled = false;
        button.textContent = original;
      }
      console.warn('[BH Cart Recommendations]', error);
    } finally { state.adding = false; }
  }

  document.addEventListener('click', event => {
    const quick = event.target.closest('[data-bh-reco-quick]');
    if (quick) {
      event.preventDefault();
      event.stopPropagation();
      quick.dataset.variantId ? add(quick, quick.dataset.variantId) : open(quick.dataset.productId);
      return;
    }
    const addButton = event.target.closest('[data-bh-reco-add]');
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();
      add(addButton);
      return;
    }
    const move = event.target.closest('[data-bh-reco-scroll]');
    if (move) {
      event.preventDefault();
      const track = document.getElementById(move.dataset.track || '');
      const width = track?.querySelector('.bh-cart-reco-tile')?.getBoundingClientRect().width || 170;
      track?.scrollBy({ left: Number(move.dataset.bhRecoScroll) * (width + 12) * 2, behavior: 'smooth' });
    }
    if (event.target.closest('[data-bh-reco-close]') || event.target.matches('[data-bh-cart-reco-modal]')) close();
  }, { signal });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') close(); }, { signal });

  let frame = 0;
  const observer = new MutationObserver(() => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(render);
  });

  function refresh() {
    state.cart = null; state.products = []; state.sections = { primary: [], secondary: [] };
    document.querySelectorAll('[data-bh-cart-recommendations-mount]').forEach(mount => {
      mount.dataset.bhRecoReady = 'false'; mount.replaceChildren();
    });
    render();
  }

  function initialize() {
    rememberProduct(); render();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  signal.addEventListener('abort', () => {
    observer.disconnect();
    clearTimeout(state.closeTimer);
    cancelAnimationFrame(frame);
  }, { once: true });

  window.BHCartRecommendations = {
    version: VERSION, controller, open, close, refresh,
    getState: () => ({ cart: state.cart, products: state.products, sections: state.sections })
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true, signal });
  else initialize();
})();
